import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Youtube, Upload, Twitch, Video, Radio, Check, Loader2, Lock, ArrowRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { videoSources } from "../mock";

const ICONS = { youtube: Youtube, twitch: Twitch, kick: Radio, vimeo: Video, upload: Upload };
const META = {
  youtube: { url: "youtube.com/watch?v=8xK2pQ_viral", info: ["Podcast", "1:24:06", "1080p"] },
  vimeo: { url: "vimeo.com/872341905", info: ["Webinar", "52:18", "4K"] },
  kick: { url: "kick.com/xqc/videos/a91f-stream", info: ["Stream VOD", "3:12:44", "1080p"] },
  twitch: { url: "twitch.tv/videos/2154789012", info: ["Gaming VOD", "4:05:31", "1080p60"] },
  upload: { url: "entrevista_final.mp4", info: ["Archivo local", "38:20", "MP4"] },
};
const STEPS = { es: ["Enlace detectado", "Transcribiendo audio", "Buscando momentos virales"], en: ["Link detected", "Transcribing audio", "Finding viral moments"] };

const Typed = ({ text }) => {
  const [n, setN] = useState(0);
  useEffect(() => { setN(0); const iv = setInterval(() => setN((v) => (v >= text.length ? v : v + 1)), 35); return () => clearInterval(iv); }, [text]);
  return <span>{text.slice(0, n)}<span className="inline-block w-[2px] h-4 bg-yellow-400 align-middle ml-0.5 animate-pulse" /></span>;
};

// Live "connector" console showing the active source being ingested
const Console = ({ src, lang, pick }) => {
  const Icon = ICONS[src.id], m = META[src.id];
  return (
    <div className="relative rounded-3xl border border-white/10 bg-zinc-950/80 p-6 sm:p-8 overflow-hidden h-full" data-testid="sources-console">
      <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full blur-3xl opacity-25 transition-colors duration-700" style={{ background: src.color }} />
      <div className="flex items-center gap-1.5 mb-6"><span className="h-2.5 w-2.5 rounded-full bg-zinc-700" /><span className="h-2.5 w-2.5 rounded-full bg-zinc-700" /><span className="h-2.5 w-2.5 rounded-full bg-zinc-700" /><span className="ml-3 text-[11px] font-mono text-zinc-500">Vitled / ingest</span></div>
      <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/60 px-4 py-3.5">
        <span className="h-9 w-9 rounded-xl grid place-items-center shrink-0 transition-colors duration-500" style={{ background: `${src.color}22`, color: src.color }}><Icon className="h-5 w-5" /></span>
        {src.id === "upload" ? <Upload className="h-4 w-4 text-zinc-500 shrink-0" /> : <Lock className="h-3.5 w-3.5 text-zinc-500 shrink-0" />}
        <span className="font-mono text-sm text-zinc-200 truncate"><Typed text={m.url} /></span>
        <span className="ml-auto hidden sm:grid h-8 w-8 rounded-lg bg-yellow-400 place-items-center shrink-0"><ArrowRight className="h-4 w-4 text-zinc-950" /></span>
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={src.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }}>
          <div className="mt-5 flex flex-wrap gap-2">
            {[src.name, ...m.info].map((x, i) => <span key={x} className={`text-xs rounded-full px-3 py-1 border ${i === 0 ? "font-semibold" : "text-zinc-400 border-white/10"}`} style={i === 0 ? { color: src.color, borderColor: `${src.color}55`, background: `${src.color}12` } : {}}>{x}</span>)}
          </div>
          <div className="mt-6 space-y-3">
            {pick(STEPS).map((s, i) => (
              <motion.div key={s} className="flex items-center gap-3 text-sm" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 + i * 0.6 }}>
                {i < 2 ? <span className="h-5 w-5 rounded-full bg-yellow-400 grid place-items-center"><Check className="h-3 w-3 text-zinc-950" strokeWidth={3} /></span> : <Loader2 className="h-5 w-5 text-yellow-400 animate-spin" />}
                <span className={i < 2 ? "text-zinc-300" : "text-white font-medium"}>{s}</span>
                {i === 2 && <span className="ml-auto h-1.5 w-28 rounded-full bg-white/10 overflow-hidden"><motion.span className="block h-full bg-yellow-400" initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ delay: 1.8, duration: 1.6 }} /></span>}
              </motion.div>
            ))}
          </div>
          <div className="mt-7 grid grid-cols-3 gap-3">
            {[[94, "0:42"], [91, "0:35"], [87, "0:58"]].map(([sc, d], i) => (
              <motion.div key={i} className="rounded-xl border border-white/10 bg-black/50 p-3" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 2.2 + i * 0.2 }}>
                <div className="aspect-[9/12] rounded-lg mb-2 relative overflow-hidden" style={{ background: `linear-gradient(160deg, ${src.color}40, #0a0a0a 70%)` }}>
                  <span className="absolute top-1.5 right-1.5 text-[9px] font-black bg-yellow-400 text-zinc-950 rounded-full px-1.5">{sc}%</span>
                  <span className="absolute bottom-2 inset-x-2 h-1 rounded bg-white/70" /><span className="absolute bottom-4 left-2 right-6 h-1 rounded bg-white/40" />
                </div>
                <p className="text-[11px] text-zinc-300 font-semibold">Clip {i + 1}</p>
                <p className="text-[10px] text-zinc-500 font-mono">{d}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default function VideoSources() {
  const { t, lang, L, pick } = useLang();
  const l = lang === "en" ? "en" : "es";
  const [active, setActive] = useState(0);
  const [hover, setHover] = useState(false);
  useEffect(() => { if (hover) return; const iv = setInterval(() => setActive((a) => (a + 1) % videoSources.length), 4200); return () => clearInterval(iv); }, [hover]);
  const src = videoSources[active];

  return (
    <section id="sources" className="py-20 sm:py-28 relative">
      <div className="max-w-6xl mx-auto px-5">
        <div className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-400 mb-4">{L("Fuentes compatibles", "Supported sources")}</p>
          <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight">{t("sources_title")}</h2>
          <p className="mt-4 text-zinc-400 text-base sm:text-lg">{t("sources_sub")}</p>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-5" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
          <div className="lg:col-span-7"><Console src={src} lang={l} pick={pick} /></div>
          <div className="lg:col-span-5 rounded-3xl border border-white/10 bg-zinc-900/40 p-2" data-testid="sources-list">
            {videoSources.map((s, i) => {
              const Icon = ICONS[s.id], on = i === active;
              return (
                <button key={s.id} onClick={() => setActive(i)} data-testid={`source-${s.id}`}
                  className={`relative w-full text-left flex items-center gap-4 rounded-2xl px-4 py-4 transition-colors duration-300 ${on ? "bg-white/[0.06]" : "hover:bg-white/[0.03]"}`}>
                  {on && <motion.span layoutId="src-bar" className="absolute left-0 top-3 bottom-3 w-[3px] rounded-full" style={{ background: s.color }} />}
                  <span className="h-11 w-11 rounded-xl grid place-items-center shrink-0 border transition-colors" style={{ background: on ? `${s.color}1f` : "transparent", color: on ? s.color : "#71717a", borderColor: on ? `${s.color}44` : "rgba(255,255,255,.08)" }}><Icon className="h-5 w-5" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2"><span className={`font-bold ${on ? "text-white" : "text-zinc-300"}`}>{s.id === "upload" ? L("Subir archivo", "File upload") : s.name}</span><code className="hidden sm:inline text-[10px] text-zinc-500 font-mono truncate">{s.sample}</code></span>
                    <span className="block text-sm text-zinc-500 mt-0.5 leading-snug">{s.desc[l]}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
